import { Flex, Grid, GridItem, Heading, Button } from '@chakra-ui/react';
import RecommendationsCard from './RecommendationsCard';
import DemoSiteModal from './DemoSiteModal';
import InviteTeamModal from './InviteTeamModal';
import { getCtaCallback } from 'features/domains/portal/content/utils';
import { useHistory } from 'react-router';
import {
  DEMO_SOLUTION_CARD_ID,
  getDeployApiClickClickLaunchBaseUrl,
} from 'features/recommendations/utils';
import {
  Link,
  Maybe,
  useGetRecommendationCardsQuery,
  useCloseRecommendationCardsMutation,
  GetRecommendationCardsDocument,
  RecommendationCard,
} from 'gql/graphql';
import { useMainStateDispatch } from 'features/main/context';
import { useEffect, useMemo, useState } from 'react';
import { transformCardData } from './utils';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from 'react-query';
import { TokenCustomClaimKeysEnum } from '@sitecore-ui/portal-singular';

const Recommendations = () => {
  const handleRecommendationClick = (card: RecommendationCard) =>
    card.cardId === DEMO_SOLUTION_CARD_ID
      ? setShowDemoModal(true)
      : onCardClickHandler(card.link);
  const history = useHistory();
  const [, dispatch] = useMainStateDispatch();
  const { data, refetch } = useGetRecommendationCardsQuery();
  const [closeRecommendationCards] = useCloseRecommendationCardsMutation();
  const [uiCards, setUiCards] = useState<RecommendationCard[]>([]);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isCreatingDemo, setIsCreatingDemo] = useState(false);
  const [platformTenantId, setPlatformTenantId] = useState<
    string | undefined
  >();
  const { getAccessTokenSilently, user } = useAuth0();
  const organizationId = user?.[TokenCustomClaimKeysEnum.ORG_ID];
  const queryClient = useQueryClient();

  const handleCreateStart = () => {
    setIsCreatingDemo(true);
    setShowDemoModal(false);
    setShowInviteModal(true);
  };
  const handleCreateDemo = async () => {
    try {
      const token = await getAccessTokenSilently({
        organization_id: organizationId,
      });
      const deployBase = getDeployApiClickClickLaunchBaseUrl();
      const url = `${deployBase}/api/organizations/v1/demo-solution`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ create: true }),
      });
      const result = await response.json();
      const newTenantId =
        result.platformTenantId ?? result.applicationId ?? result.id;
      setPlatformTenantId(newTenantId);
      queryClient.invalidateQueries([
        'tenants-by-org-and-productcode',
        organizationId,
      ]);
      refetch();
    } catch (err) {
      console.error('Demo site creation failed', err);
    } finally {
      setIsCreatingDemo(false);
    }
  };
  const recommendationCards = useMemo(() => {
    const partialCards =
      data?.personalizedContent?.recommendationCards.slice(0, 3) ?? [];
    return transformCardData(partialCards);
  }, [data]);

  useEffect(() => setUiCards(recommendationCards), [recommendationCards]);

  const clearAllHandler = () => {
    const cardIds = uiCards.map(({ cardId }) => cardId);
    setUiCards([]);
    closeRecommendationCards({
      variables: {
        cardIds,
      },
      refetchQueries: [GetRecommendationCardsDocument],
    });
  };

  const onCardClickHandler = (link: Maybe<Link> | undefined) => {
    if (!link) return;

    return getCtaCallback({ link, history, dispatch: dispatch })();
  };

  const onCloseClickHandler = (id: string) => {
    const filteredCards = uiCards.filter(({ cardId }) => cardId !== id);
    setUiCards(filteredCards);
    closeRecommendationCards({
      variables: {
        cardIds: [id],
      },
      refetchQueries: [GetRecommendationCardsDocument],
    });
  };

  if (uiCards.length === 0) return null;

  return (
    <Flex
      data-testid='recommendations'
      flexDirection='column'
      width='full'
      gap={5}
    >
      <Flex width='full' justifyContent='space-between'>
        <Heading size='md' noOfLines={1}>
          Recommended for you
        </Heading>
        <Button variant='link' onClick={clearAllHandler}>
          Clear all
        </Button>
      </Flex>
      <Grid
        width='full'
        templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(3, 1fr)' }}
        gap={4}
      >
        {uiCards.map((c) => (
          <GridItem w='full' h='full' key={c.cardId}>
            <RecommendationsCard
              cardId={c.cardId}
              icon={c.icon}
              title={c.title}
              description={c.description}
              onClick={() => handleRecommendationClick(c)}
              onClose={() => onCloseClickHandler(c.cardId)}
            />
          </GridItem>
        ))}
      </Grid>
      <DemoSiteModal
        isOpen={showDemoModal}
        onClose={() => setShowDemoModal(false)}
        isCreating={isCreatingDemo}
        onCreate={() => {
          handleCreateStart();
          handleCreateDemo();
        }}
      />
      <InviteTeamModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSend={() => setShowInviteModal(false)}
        isGiveAccessDisabled={isCreatingDemo || !platformTenantId}
        platformTenantId={platformTenantId}
      />
    </Flex>
  );
};

export default Recommendations;
